// src/services/api.ts
/**
 * Cliente API base para comunicación con el backend.
 * Wrapper sobre fetch con manejo de auth, errores y tipos.
 * Access token in Zustand memory, refresh token in httpOnly cookie.
 */
import { useAuthStore } from '@/stores/authStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Token management — reads from Zustand memory, no localStorage

export function saveAccessToken(token: string): void {
  useAuthStore.getState().setAccessToken(token);
}

export function clearAuth(): void {
  useAuthStore.getState().logout();
}

export function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}

// Error types
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public data?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Request options type
interface RequestOptions extends RequestInit {
  requiresAuth?: boolean;
}

// Response type
interface ApiResponse<T> {
  data: T;
  status: number;
}

// API Client class
class ApiClient {
  private baseUrl: string;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {},
    isRetry = false
  ): Promise<ApiResponse<T>> {
    const { requiresAuth = true, ...fetchOptions } = options;
    const url = `${this.baseUrl}${endpoint}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    };

    // Add auth token from memory
    if (requiresAuth) {
      const token = getAccessToken();
      if (token) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        credentials: 'include', // Send httpOnly cookies
      });

      // Handle 401 or 403-without-token — try refresh (once only)
      // FastAPI HTTPBearer(auto_error=True) returns 403 "Not authenticated" when
      // Authorization header is missing (after hard refresh, accessToken lost from memory).
      // Genuine permission 403s (e.g. require_admin) have different detail messages.
      const needsRefresh =
        (response.status === 401 ||
          (response.status === 403 && !getAccessToken())) &&
        requiresAuth &&
        !isRetry;

      if (needsRefresh) {
        const refreshed = await this.tryRefreshToken();
        if (refreshed) {
          return this.request<T>(endpoint, options, true);
        }
        // Logout already handled inside doRefreshToken for auth failures (401/403).
        // For network/5xx errors, user stays logged in — throw without logout.
        throw new ApiError('Sesión expirada', 401);
      }

      // Parse response
      let data: T | undefined;
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        data = await response.json();
      }

      if (!response.ok) {
        const rawDetail = (data as { detail?: unknown })?.detail;
        let errorMessage: string;

        if (typeof rawDetail === 'string') {
          errorMessage = rawDetail;
        } else if (Array.isArray(rawDetail)) {
          // FastAPI 422 validation errors: [{loc, msg, type}, ...]
          errorMessage = rawDetail
            .map((err: { msg?: string; loc?: string[] }) => {
              const field = err.loc?.filter((l) => l !== 'body').join('.') || '';
              return field ? `${field}: ${err.msg}` : (err.msg || '');
            })
            .filter(Boolean)
            .join('; ') || 'Error de validación';
        } else {
          const statusMessages: Record<number, string> = {
            403: 'Acceso denegado',
            404: 'Recurso no encontrado',
            500: 'Error interno del servidor',
            502: 'Servidor no disponible temporalmente',
            503: 'Servicio en mantenimiento',
          };
          errorMessage =
            (data as { message?: string })?.message ||
            statusMessages[response.status] ||
            `Error del servidor (${response.status})`;
        }

        throw new ApiError(errorMessage, response.status, data);
      }

      return { data: data as T, status: response.status };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        error instanceof Error ? error.message : 'Error de conexión',
        0
      );
    }
  }

  private tryRefreshToken(): Promise<boolean> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.doRefreshToken().finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  private async doRefreshToken(): Promise<boolean> {
    let response: Response;
    try {
      // Include expired access token so backend preserves `env` claim
      const expiredToken = getAccessToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (expiredToken) {
        headers['Authorization'] = `Bearer ${expiredToken}`;
      }
      response = await fetch(`${this.baseUrl}/auth/refresh`, {
        method: 'POST',
        headers,
        credentials: 'include', // Send httpOnly cookie
      });
    } catch (error) {
      // Network error — do NOT logout, session may still be valid
      console.error('Token refresh network error:', error);
      return false;
    }

    if (response.ok) {
      const data = await response.json();
      useAuthStore.getState().setAccessToken(data.access_token);
      return true;
    }

    // Only logout on definitive auth failures (401, 403)
    if (response.status === 401 || response.status === 403) {
      useAuthStore.getState().logout();
      // Force redirect — prevents stale UI showing empty data
      window.location.href = '/login';
    }
    // For 5xx, do not logout — server may recover
    return false;
  }

  // HTTP methods
  async get<T>(endpoint: string, requiresAuth = true): Promise<T> {
    const response = await this.request<T>(endpoint, { method: 'GET', requiresAuth });
    return response.data;
  }

  async post<T>(endpoint: string, body?: unknown, requiresAuth = true): Promise<T> {
    const response = await this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      requiresAuth,
    });
    return response.data;
  }

  async put<T>(endpoint: string, body?: unknown, requiresAuth = true): Promise<T> {
    const response = await this.request<T>(endpoint, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
      requiresAuth,
    });
    return response.data;
  }

  async patch<T>(endpoint: string, body?: unknown, requiresAuth = true): Promise<T> {
    const response = await this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
      requiresAuth,
    });
    return response.data;
  }

  async delete<T>(endpoint: string, requiresAuth = true): Promise<T> {
    const response = await this.request<T>(endpoint, { method: 'DELETE', requiresAuth });
    return response.data;
  }
}

// Export singleton instance
export const api = new ApiClient(API_BASE_URL);
